import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, SquarePen, ShoppingBag, LogOut, Shield, BarChart3, Zap, UserCheck, Users, Building2, Plug, TrendingUp, ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useIsOrgAdmin } from '../hooks/useIsOrgAdmin';
import { useTranslation } from 'react-i18next';
import CataxIcon from '../assets/CataxIcon';


interface MobileHeaderProps {
  title?: string;
  showMenu?: boolean;
  showBack?: boolean;
  onBackClick?: () => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showMenu = true,
  showBack = false,
  onBackClick
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentUser, logout, isImpersonating, impersonatedUser, originalAdmin, stopImpersonation } = useAuth();
  const isOrgAdmin = useIsOrgAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isROIAllowed = currentUser?.roi_calculator_enabled ?? false;
  const roiNavPath = (!currentUser?.roi_b2b_enabled && currentUser?.roi_b2c_enabled)
    ? '/roi-calculator/b2c'
    : '/roi-calculator/b2b';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    {
      icon: SquarePen,
      label: t('homepage.buttons.startNewChat', { defaultValue: 'New chat' }),
      path: '/',
      show: true
    },
    {
      icon: BarChart3,
      label: t('header.dashboard', { defaultValue: 'Dashboard' }),
      path: '/dashboard',
      show: currentUser
    },
    {
      icon: ShoppingBag,
      label: t('header.orders', { defaultValue: 'Orders' }),
      path: '/orders',
      show: currentUser
    },
    {
      icon: TrendingUp,
      label: t('header.roiCalculator', { defaultValue: 'ROI Calculator' }),
      path: roiNavPath,
      show: !!currentUser && isROIAllowed
    },
    {
      icon: Zap,
      label: 'Workflows',
      path: '/workflows',
      show: currentUser
    },
    {
      icon: BarChart3,
      label: t('header.usagePlan', { defaultValue: 'Usage & Plan' }),
      path: '/usage',
      show: currentUser && isOrgAdmin
    },
    {
      icon: Users,
      label: t('header.team', { defaultValue: 'Team' }),
      path: '/team',
      show: currentUser
    },
    {
      icon: Building2,
      label: t('header.organization', { defaultValue: 'Organization' }),
      path: '/organization',
      show: currentUser
    },
    {
      icon: Plug,
      label: t('header.connections', { defaultValue: 'Connections' }),
      path: '/settings',
      show: currentUser
    },
    {
      icon: Shield,
      label: t('header.adminPanel', { defaultValue: 'Admin Panel' }),
      path: '/admin',
      show: currentUser && (currentUser as any)?.role === 'admin'
    }
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    setIsMenuOpen(false);
    navigate('/admin/users');
  };

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Mobile Header - Only visible on mobile */}
      <div className="mobile-header lg:hidden relative z-50 bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 min-h-14">
          {/* Left: Menu Button */}
          {showBack ? (
            <button
              onClick={onBackClick || (() => navigate(-1))}
              className="mobile-touch-target p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('common.back', { defaultValue: 'Back' })}
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
          ) : showMenu ? (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-touch-target p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          ) : <div className="w-11" aria-hidden="true" />}

          {/* Center/Right: Logo or Back Button and Title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">

            <button
              onClick={() => navigate('/')}
              className="mobile-touch-target flex items-center justify-center rounded-lg"
              aria-label="Home"
            >
              <CataxIcon className="w-8 h-8 text-primary-600" />
            </button>

            {title && (
              <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
            )}
          </div>

        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 animate-fadeIn"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="mobile-menu-panel lg:hidden fixed top-0 left-0 bottom-0 w-[min(19rem,88vw)] bg-white shadow-2xl z-50 overflow-y-auto overscroll-contain animate-slideInLeft">
            {/* Menu Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CataxIcon className="w-6 h-6 text-primary-600" />
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="mobile-touch-target p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('common.close', { defaultValue: 'Close menu' })}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* User Info */}
            {currentUser && (
              <div className="px-4 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  {currentUser.picture ? (
                    <img
                      src={currentUser.picture}
                      alt={currentUser.displayName || 'User'}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-sm">
                        {currentUser.displayName?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {currentUser.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {currentUser.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Impersonation Banner - Stop Impersonating */}
            {isImpersonating && impersonatedUser && originalAdmin && (
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-900">
                      Impersonating: <strong>{impersonatedUser.displayName || impersonatedUser.email}</strong>
                    </span>
                  </div>
                  <button
                    onClick={handleStopImpersonation}
                    className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-2 py-2 rounded-lg transition-colors text-xs font-medium"
                  >
                    <X className="w-3 h-3" />
                    Stop Impersonating
                  </button>
                </div>
              </div>
            )}

            {/* Start New Chat Button - only shown when not on homepage
            {currentUser && location.pathname !== '/' && (
              <div className="px-4 py-3 border-b border-gray-200">
                <button
                  onClick={() => handleNavigate('/')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  <PenSquare className="w-4 h-4" />
                  <span>{t('header.startNewChat', { defaultValue: 'Start New Chat' })}</span>
                </button>
              </div>
            )} */}

            {/* Menu Items */}
            <nav className="py-2">
              {menuItems.filter(item => item.show).map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);

                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    className={`mobile-touch-target w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${isActive
                      ? 'bg-primary-50 text-primary-600 border-r-4 border-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-gray-500'}`} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
            {/* Sign Out Button */}
            {currentUser && (
              <div className="px-4 py-4 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{t('header.signOut', { defaultValue: 'Sign Out' })}</span>
                </button>
              </div>
            )}

            {/* Sign In Button for non-authenticated users */}
            {!currentUser && (
              <div className="px-4 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors font-medium"
                >
                  {t('header.signIn', { defaultValue: 'Sign In' })}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default MobileHeader;
